import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion, type Variants } from "framer-motion";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";
import {
  Key, Check, X, RefreshCw, ShieldCheck, AlertTriangle, Copy, Lock, Plus,
} from "lucide-react";
import type { RootState } from "../../redux/store";

/* ─── THEME — no stylesheet anywhere on this page ────────────────────────
   Everything below used to live in a `<style>{CSS}</style>` block keyed
   off .ak-* classNames. That's gone: every element now carries its own
   `style={{...}}` object built at render time, and the handful of pieces
   that need a hover/press state (buttons, beads, chips) are small local
   components below that track that state with useState and recompute
   their own inline style — no className, no injected CSS, nothing that a
   sanitizer or a strict style-src CSP could strip out from under it.
   Colors/shadows are unchanged from the previous pass. ─────────────────── */

const INK        = "#181829";
const INK_DEEP   = "#0E0E1C";
const SLATE      = "#6B6E89";
const FAINT      = "#9DA0BC";
const PEARL      = "#FFFFFF";
const MIST       = "#EEEFF9";
const CHROME     = "#E3E5F3";
const CHROME_DIM = "#C9CCE2";
const RED        = "#FF5C5C";
const RED_DEEP   = "#B23434";

const EBUOS_BLUE   = "#4C6FEE";
const EBUOS_VIOLET = "#8B5BE8";
const EBUOS_PINK   = "#E93E6D";
const EBUOS_ORANGE = "#F3821E";
const EBUOS_YELLOW = "#E8C51E";
const EBUOS_GREEN  = "#3FBE55";
const EBUOS_GRADIENT = `linear-gradient(90deg, ${EBUOS_BLUE} 0%, ${EBUOS_VIOLET} 18%, ${EBUOS_PINK} 38%, ${EBUOS_ORANGE} 60%, ${EBUOS_YELLOW} 80%, ${EBUOS_GREEN} 100%)`;

const ACCENT      = EBUOS_PINK;
const ACCENT_DEEP = "#C22753";
const ACCENT_TINT = "#FDEAF0";
const VIOLET_DEEP = "#6B3FC0";

const GOLD = { base: EBUOS_YELLOW, lit: "#F3DB6B", deep: "#B99A05", soft: "#FBF6DC" };
const SAGE      = EBUOS_GREEN;
const SAGE_DEEP = "#2C9540";
const SAGE_SOFT = "#E7F9EC";

const BEVEL = `inset 0 1.5px 0 rgba(255,255,255,.85), inset 0 -1.5px 0 rgba(20,20,43,.05)`;
const DOT = "\u00b7";

const FONT_LINK =
  "https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap";

const API_BASE = "https://europe-west3-ebuosystem.cloudfunctions.net";
/* Region matches functions.setGlobalOptions() in auth-webhooks/index.js —
   europe-west1 (used elsewhere in this app's older pages) is wrong and
   404s against every real function. */

const db = getFirestore();

/* ═══ KEY DETECTION ═══
   Mirrors OrganizationOverview.tsx: pk_live/pk_test are stored in the
   clear, everything else (sk/wh/rk) is only ever a hash after the one
   moment registerMerchant/rotateMerchantKeys returns it raw. "Do keys
   exist" is answered by any of these fields being present. */
const KEY_FIELDS = [
  "pk_live", "pk_test",
  "pk_live_hash", "pk_test_hash",
  "sk_live_hash", "sk_test_hash",
  "rk_live_hash", "rk_test_hash",
  "wh_live_hash", "wh_test_hash",
];

const extractKeys = (raw: any): Record<string, any> | null => {
  if (!raw || typeof raw !== "object") return null;
  for (const candidate of [raw.keys, raw.apiKeys, raw]) {
    if (candidate && typeof candidate === "object" && KEY_FIELDS.some((f) => candidate[f])) {
      return candidate as Record<string, any>;
    }
  }
  return null;
};

interface MerchantDoc {
  businessName?: string;
  accountId?: string;
  status?: "active" | "pending" | "suspended";
  mode?: "test" | "live";
  liveEnabled?: boolean;
  userId?: string;
  keysGeneratedAt?: any;
  updatedAt?: any;
}

type KeyState = {
  loading: boolean;
  merchant: MerchantDoc | null;
  keys: Record<string, any> | null;
};

/* Same realtime pattern as useKeyStatus in OrganizationOverview.tsx,
   scoped to one merchant instead of a list — watches the merchant doc so
   the page flips from "no keys" to "keys live" the moment
   registerMerchant/rotateMerchantKeys finishes writing, with no manual
   refetch. Falls back to merchantKeys/{merchantId} if the map lives in
   its own collection. */
function useMerchantKeys(merchantId: string | undefined) {
  const [state, setState] = useState<KeyState>({ loading: true, merchant: null, keys: null });

  useEffect(() => {
    if (!merchantId) { setState({ loading: false, merchant: null, keys: null }); return; }
    let alive = true;
    setState((p: KeyState) => ({ ...p, loading: true }));

    const unsub = onSnapshot(
      doc(db, "merchants", merchantId),
      async (snap: any) => {
        if (!alive) return;
        const raw = (snap.data() as MerchantDoc | undefined) ?? {};
        let keys = extractKeys(raw);

        if (!keys) {
          try {
            const alt = await getDoc(doc(db, "merchantKeys", merchantId));
            if (alt.exists()) keys = extractKeys(alt.data());
          } catch { /* collection may not exist — that's fine */ }
        }
        if (!alive) return;
        setState({ loading: false, merchant: raw, keys });
      },
      () => { if (alive) setState({ loading: false, merchant: null, keys: null }); }
    );

    return () => { alive = false; unsub(); };
  }, [merchantId]);

  return state;
}

/* ─── ANIMATION ─── */
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const cardVariant: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};
const modalOverlay: Variants = { hidden: { opacity: 0 }, visible: { opacity: 1 }, exit: { opacity: 0 } };
const modalContent: Variants = {
  hidden: { opacity: 0, scale: 0.94, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.4, ease: EASE } },
  exit: { opacity: 0, scale: 0.96, y: 10 },
};

/* Long names come back from registerMerchant/rotateMerchantKeys; short
   names are what's on the doc. Look for both so one modal covers either
   response shape. */
const KEY_LABELS: { label: string; key: string; alt: string; group: "publishable" | "secret" | "webhook" | "restricted" }[] = [
  { label: "Publishable key (live)", key: "publishable_key_live", alt: "pk_live", group: "publishable" },
  { label: "Publishable key (test)", key: "publishable_key_test", alt: "pk_test", group: "publishable" },
  { label: "Secret key (live)", key: "secret_key_live", alt: "sk_live", group: "secret" },
  { label: "Secret key (test)", key: "secret_key_test", alt: "sk_test", group: "secret" },
  { label: "Webhook secret (live)", key: "webhook_secret_live", alt: "wh_live", group: "webhook" },
  { label: "Webhook secret (test)", key: "webhook_secret_test", alt: "wh_test", group: "webhook" },
  { label: "Restricted key (live)", key: "restricted_key_live", alt: "rk_live", group: "restricted" },
  { label: "Restricted key (test)", key: "restricted_key_test", alt: "rk_test", group: "restricted" },
];

const ROTATE_TYPES: { key_type: "all" | "publishable" | "secret" | "webhook" | "restricted"; label: string }[] = [
  { key_type: "all", label: "All keys" },
  { key_type: "publishable", label: "Publishable" },
  { key_type: "secret", label: "Secret" },
  { key_type: "webhook", label: "Webhook" },
  { key_type: "restricted", label: "Restricted" },
];

const mask = (v?: string) => (v && v.length > 18 ? `${v.slice(0, 12)}…${v.slice(-4)}` : v || "—");

/* ═══════════════════════════════════════════════════════════════════════
   LOCAL PRESENTATIONAL COMPONENTS — replace every .ak-* class.
   Each one owns its own hover/press state and returns a style object;
   nothing here reads from or writes to a stylesheet.
   ═══════════════════════════════════════════════════════════════════════ */

const mono = (extra?: React.CSSProperties): React.CSSProperties => ({
  fontFamily: "'IBM Plex Mono',ui-monospace,monospace",
  fontWeight: 600, fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase",
  ...extra,
});

const heading = (size: number, extra?: React.CSSProperties): React.CSSProperties => ({
  fontFamily: "'Fredoka',system-ui,sans-serif", fontWeight: 600, margin: 0,
  letterSpacing: "-.03em", lineHeight: 1, fontSize: size, ...extra,
});

const plateStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
  position: "relative", background: PEARL, borderRadius: 26, padding: 22, overflow: "hidden",
  boxShadow: `${BEVEL}, 0 8px 0 ${CHROME_DIM}, 0 10px 24px -16px rgba(20,20,43,.14)`,
  ...extra,
});

/* Flat color circle, glossy top highlight, solid colored offset shadow.
   Renders as a plain <span> when there's no onClick (decorative icon),
   or a real <button> with hover-lift / press-flat when there is. */
function Bead({
  children, size = 46, tone = "gradient", bg, color, offset, onClick, ariaLabel, title, style,
}: {
  children: React.ReactNode; size?: number; tone?: "gradient" | "chrome";
  bg?: string; color?: string; offset?: string;
  onClick?: () => void; ariaLabel?: string; title?: string; style?: React.CSSProperties;
}) {
  const reduceMotion = useReducedMotion();
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  const resolvedBg = bg ?? (tone === "chrome"
    ? `radial-gradient(circle at 34% 26%, #fff 0%, ${CHROME} 46%, ${CHROME_DIM} 100%)`
    : EBUOS_GRADIENT);
  const resolvedColor = color ?? (tone === "chrome" ? INK_DEEP : "#fff");
  const resolvedOffset = offset ?? (tone === "chrome" ? CHROME_DIM : VIOLET_DEEP);

  const lift = !onClick ? 0 : active ? 3 : hover ? -3 : 0;
  const shadowY = !onClick ? 6 : active ? 1 : hover ? 9 : 6;

  const base: React.CSSProperties = {
    width: size, height: size, borderRadius: "50%", flexShrink: 0, padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: resolvedBg, color: resolvedColor,
    boxShadow: `${BEVEL}, 0 ${shadowY}px 0 ${resolvedOffset}`,
    border: "none",
    transform: `translateY(${lift}px)`,
    transition: reduceMotion ? "none" : "transform .2s cubic-bezier(.2,.9,.3,1.4), box-shadow .2s",
    ...style,
  };

  if (!onClick) return <span style={base}>{children}</span>;

  return (
    <button
      type="button" onClick={onClick} aria-label={ariaLabel} title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{ ...base, cursor: "pointer" }}
    >
      {children}
    </button>
  );
}

function Btn({
  children, onClick, disabled, variant = "default", size = "md", style, ariaLabel,
}: {
  children: React.ReactNode; onClick?: () => void; disabled?: boolean;
  variant?: "default" | "primary" | "ink" | "ghost"; size?: "md" | "sm";
  style?: React.CSSProperties; ariaLabel?: string;
}) {
  const reduceMotion = useReducedMotion();
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const isSm = size === "sm";

  let bg: string = PEARL, color: string = INK_DEEP, offset: string = ACCENT_DEEP;
  if (variant === "primary") { bg = EBUOS_GRADIENT; color = "#fff"; offset = VIOLET_DEEP; }
  if (variant === "ink") { bg = ACCENT; color = "#fff"; offset = ACCENT_DEEP; }
  if (variant === "ghost") { bg = hover ? PEARL : MIST; color = INK_DEEP; }

  const lift = disabled ? 0 : active ? (variant === "ghost" ? 2 : 3) : hover ? (variant === "ghost" ? -2 : -3) : 0;

  const shadow = variant === "ghost"
    ? `inset 0 0 0 2.5px ${hover ? ACCENT : CHROME_DIM}`
    : `${BEVEL}, 0 ${active ? 1 : hover ? (isSm ? 6 : 9) : (isSm ? 4 : 6)}px 0 ${offset}`;

  return (
    <button
      type="button" onClick={onClick} disabled={disabled} aria-label={ariaLabel}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 9, whiteSpace: "nowrap",
        fontFamily: "'Fredoka',sans-serif", fontWeight: 600,
        fontSize: isSm ? 12.5 : 14, padding: isSm ? "10px 17px" : "13px 22px", borderRadius: 999,
        background: bg, color, boxShadow: shadow, border: "none",
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.65 : 1,
        transform: `translateY(${lift}px)`,
        transition: reduceMotion ? "none" : "transform .18s cubic-bezier(.2,.9,.3,1.4), box-shadow .18s, background .15s",
        textShadow: variant === "primary" ? "0 1px 2px rgba(0,0,0,.18)" : "none",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function Chip({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  const reduceMotion = useReducedMotion();
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  return (
    <button
      type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 14px", borderRadius: 999,
        fontFamily: "'Fredoka',sans-serif", fontWeight: 600, fontSize: 12.5,
        color: hover ? ACCENT : INK_DEEP, background: PEARL, border: "none", cursor: "pointer",
        boxShadow: `inset 0 0 0 2.5px ${hover ? ACCENT : CHROME_DIM}, 0 ${active ? 1 : 4}px 0 ${CHROME_DIM}66`,
        transform: `translateY(${active ? 2 : hover ? -2 : 0}px)`,
        transition: reduceMotion ? "none" : "transform .16s cubic-bezier(.2,.9,.3,1.4), box-shadow .16s, color .15s",
      }}
    >
      {children}
    </button>
  );
}

function CopyIconBtn({
  children, onClick, ariaLabel, title,
}: { children: React.ReactNode; onClick?: () => void; ariaLabel?: string; title?: string }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button" onClick={onClick} aria-label={ariaLabel} title={title}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", color: hover ? ACCENT : FAINT, padding: 4, borderRadius: 8, flexShrink: 0,
        background: hover ? ACCENT_TINT : "transparent", border: "none", cursor: "pointer",
        transition: "color .15s, background .15s",
      }}
    >
      {children}
    </button>
  );
}

function Spinner({ size = 36 }: { size?: number }) {
  const reduceMotion = useReducedMotion();
  const thick = size > 20;
  return (
    <motion.div
      style={{
        width: size, height: size, borderRadius: "50%",
        border: `${thick ? 4 : 2}px solid ${CHROME}`, borderTopColor: ACCENT,
        margin: thick ? "0 auto" : 0, display: "inline-block",
      }}
      animate={reduceMotion ? { rotate: 0 } : { rotate: 360 }}
      transition={reduceMotion ? { duration: 0 } : { repeat: Infinity, ease: "linear", duration: 0.8 }}
    />
  );
}

/* ═══════════════════════════════════════
   Route: /merchant/:merchantId/api
   ═══════════════════════════════════════ */
export default function API() {
  const { merchantId: merchantIdParam } = useParams<{ merchantId: string }>();
  const currentMerchantId = useSelector((s: RootState) => (s as any).merchant?.currentMerchant?.merchantId ?? "");
  const merchantId = merchantIdParam || currentMerchantId;

  const { loading, merchant, keys } = useMerchantKeys(merchantId);

  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 800 : false);
  const [generating, setGenerating] = useState(false);
  const [rotating, setRotating] = useState<string | null>(null);
  const [rotateTarget, setRotateTarget] = useState<{ key_type: typeof ROTATE_TYPES[number]["key_type"]; label: string } | null>(null);
  const [secretInput, setSecretInput] = useState("");
  const [secretFocused, setSecretFocused] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [keysModal, setKeysModal] = useState<{ accountId?: string; keys: Record<string, any> } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const copyValue = useCallback((id: string, value: string) => {
    if (!value || value === "—") return;
    navigator.clipboard.writeText(value).then(() => setCopied(id)).catch(() => {});
    setTimeout(() => setCopied(null), 2000);
  }, []);

  const hasKeys = !!keys;
  const pkLive = keys?.pk_live;
  const pkTest = keys?.pk_test;
  const secretCount = keys
    ? ["sk_live_hash", "sk_test_hash", "rk_live_hash", "rk_test_hash", "wh_live_hash", "wh_test_hash"]
        .filter((f) => keys[f]).length
    : 0;

  /* First-time generation only needs the signed-in user's own Firebase ID
     token — this is the one call in this whole page that a browser can
     make honestly, because registerMerchant authenticates that way. */
  const generateKeys = async () => {
    if (!merchantId) return;
    setGenerating(true);
    setError(null);
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error("Sign in again to generate keys.");
      const token = await currentUser.getIdToken(true);

      const { data } = await axios.post(
        `${API_BASE}/registerMerchant`,
        { userId: merchant?.userId || currentUser.uid, merchantId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKeysModal({ accountId: data.accountId, keys: data.keys });
    } catch (err: any) {
      console.error("Error generating keys:", err);
      setError(err?.response?.data?.error?.message || err?.message || "Keys couldn't be generated. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  /* rotateMerchantKeys authenticates with a SECRET key
     (authenticateRequest → auth.isSecret), not a Firebase ID token — so
     unlike registerMerchant, this page cannot call it on the merchant's
     behalf without the merchant handing over a secret key. Asking them
     to paste the one they already saved at generation time is the only
     honest way to satisfy that requirement from a browser today. The
     real fix is a backend change (let rotateMerchantKeys also accept a
     Firebase ID token, the way registerMerchant does) — this UI works
     around the gap instead of hiding it. */
  const confirmRotate = async () => {
    if (!rotateTarget || !merchantId) return;
    setRotateError(null);
    if (!secretInput.trim().startsWith("sk_")) {
      setRotateError("Enter the secret key you saved when these keys were generated (starts with sk_live_ or sk_test_).");
      return;
    }
    setRotating(rotateTarget.key_type);
    try {
      const { data } = await axios.post(
        `${API_BASE}/rotateMerchantKeys`,
        { key_type: rotateTarget.key_type },
        { headers: { Authorization: `Bearer ${secretInput.trim()}` } }
      );
      setKeysModal({ accountId: merchant?.accountId, keys: data.keys });
      setRotateTarget(null);
      setSecretInput("");
    } catch (err: any) {
      console.error("Error rotating keys:", err);
      setRotateError(err?.response?.data?.error?.message || err?.message || "Rotation failed. Check the key and try again.");
    } finally {
      setRotating(null);
    }
  };

  const st = merchant?.status ?? "active";
  const statusColor = st === "active" ? SAGE_DEEP : st === "pending" ? GOLD.deep : RED_DEEP;
  const statusBg = st === "active" ? SAGE_SOFT : st === "pending" ? GOLD.soft : "#FFECEC";

  return (
    <div style={{ position: "relative", fontFamily: "'Space Grotesk',system-ui,sans-serif", fontWeight: 500, color: INK, background: "transparent", WebkitFontSmoothing: "antialiased" } as React.CSSProperties}>
      {/* Fonts load via a real stylesheet link, not an injected <style> block —
          this survives sanitizers/CSP setups that strip inline style tags. */}
      <link rel="stylesheet" href={FONT_LINK} />

      <div style={{ maxWidth: 760, margin: "0 auto", padding: isMobile ? "24px 16px 40px" : "40px 40px 60px" }}>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 30, flexWrap: "wrap" }}>
          <div>
            <h1 style={heading(isMobile ? 24 : 28)}>API keys</h1>
            <p style={mono({ color: FAINT, marginTop: 8, textTransform: "none", letterSpacing: 0, fontSize: 12 })}>
              {merchant?.businessName || "This business"} {DOT} {loading ? "Loading" : hasKeys ? "keys live" : "no keys yet"}
            </p>
          </div>
          <span style={mono({ fontSize: 9, color: statusColor, borderRadius: 999, padding: "6px 13px", background: statusBg })}>
            {st}
          </span>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              style={plateStyle({ boxShadow: `${BEVEL}, 0 0 0 2.5px ${RED}, 0 6px 0 ${RED_DEEP}`, marginBottom: 24, padding: "14px 20px", fontSize: 13, color: RED_DEEP, fontWeight: 600 })}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {loading && (
          <div style={plateStyle({ textAlign: "center", padding: 60 })}>
            <Spinner size={36} />
            <div style={mono({ color: FAINT, marginTop: 14 })}>Checking keys…</div>
          </div>
        )}

        {!loading && !hasKeys && (
          <motion.div variants={cardVariant} initial="hidden" animate="visible"
            style={plateStyle({ textAlign: "center", padding: isMobile ? "50px 20px" : "70px 40px" })}>
            <Bead size={60} style={{ margin: "0 auto 20px" }}><Key size={26} strokeWidth={2.4} /></Bead>
            <h2 style={heading(22, { marginBottom: 10 })}>No keys yet</h2>
            <p style={{ color: SLATE, fontSize: 13, lineHeight: 1.7, maxWidth: 400, margin: "0 auto 24px" }}>
              Generate a publishable/secret/webhook/restricted key set to start authenticating requests.
            </p>
            <Btn variant="primary" onClick={generateKeys} disabled={generating}>
              {generating ? <><Spinner size={14} />Generating…</> : <><Plus size={15} strokeWidth={2.8} />Generate keys</>}
            </Btn>
          </motion.div>
        )}

        {!loading && hasKeys && (
          <motion.div variants={cardVariant} initial="hidden" animate="visible"
            style={plateStyle({ display: "flex", flexDirection: "column", gap: 18 })}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <ShieldCheck size={16} color={SAGE_DEEP} strokeWidth={2.6} />
              <span style={{ fontSize: 13.5, color: SAGE_DEEP, fontWeight: 700 }}>Keys are live</span>
              <span style={mono({ color: FAINT, marginLeft: "auto" })}>{secretCount} secrets hashed</span>
            </div>

            <div style={{ background: MIST, borderRadius: 18, padding: "14px 16px" }}>
              {[
                { id: "pk_live", label: "pk_live", value: pkLive },
                { id: "pk_test", label: "pk_test", value: pkTest },
              ].filter((r) => r.value).map((r) => (
                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                  <span style={mono({ color: FAINT, width: 60, flexShrink: 0 })}>{r.label}</span>
                  <span style={mono({ flex: 1, minWidth: 0, fontSize: 11, color: INK, textTransform: "none", letterSpacing: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })}>
                    {mask(r.value)}
                  </span>
                  <CopyIconBtn onClick={() => copyValue(r.id, r.value!)} ariaLabel={`Copy ${r.label}`} title={`Copy ${r.label}`}>
                    {copied === r.id ? <Check size={13} strokeWidth={3} color={SAGE_DEEP} /> : <Copy size={13} strokeWidth={2.6} />}
                  </CopyIconBtn>
                </div>
              ))}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                <span style={mono({ color: FAINT, width: 60, flexShrink: 0 })}>secret</span>
                <span style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, fontSize: 11.5, color: FAINT }}>
                  <Lock size={11} strokeWidth={2.6} /> hashed — never shown again after generation
                </span>
              </div>
            </div>

            <div>
              <div style={mono({ color: FAINT, marginBottom: 10 })}>Rotate</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {ROTATE_TYPES.map((t) => (
                  <Chip key={t.key_type} onClick={() => { setRotateTarget(t); setSecretInput(""); setRotateError(null); }}>
                    <RefreshCw size={12} strokeWidth={2.6} />{t.label}
                  </Chip>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* ─── ROTATE CONFIRM (needs the merchant's own secret key) ─── */}
      <AnimatePresence>
        {rotateTarget && (
          <motion.div variants={modalOverlay} initial="hidden" animate="visible" exit="exit"
            onClick={() => { setRotateTarget(null); setSecretInput(""); }}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(6,14,56,.6)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <motion.div variants={modalContent} initial="hidden" animate="visible" exit="exit"
              onClick={(e: React.MouseEvent) => e.stopPropagation()} role="dialog" aria-label="Rotate keys"
              style={plateStyle({ width: "100%", maxWidth: 440, padding: isMobile ? 24 : 32 })}>
              <Bead size={60} bg={GOLD.soft} color={GOLD.deep} offset={CHROME_DIM} style={{ margin: "0 auto 16px" }}>
                <AlertTriangle size={24} strokeWidth={2.4} />
              </Bead>
              <h2 style={heading(21, { marginBottom: 10, textAlign: "center" })}>Rotate {rotateTarget.label.toLowerCase()} keys?</h2>
              <p style={{ fontSize: 13, color: SLATE, lineHeight: 1.7, marginBottom: 18, textAlign: "center" }}>
                The current {rotateTarget.label.toLowerCase()} keys stop working immediately. Rotating requires the secret
                key you saved when these keys were first generated — this endpoint doesn't accept your sign-in.
              </p>
              <label style={mono({ color: FAINT, display: "block", marginBottom: 6 })}>Secret key</label>
              <input
                type="password" autoFocus value={secretInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSecretInput(e.target.value)}
                onFocus={() => setSecretFocused(true)}
                onBlur={() => setSecretFocused(false)}
                placeholder="sk_live_… or sk_test_…"
                style={{
                  width: "100%", boxSizing: "border-box", background: MIST, borderRadius: 12, border: "none",
                  padding: "12px 14px", fontSize: 13, fontFamily: "'IBM Plex Mono',ui-monospace,monospace", color: INK,
                  boxShadow: `inset 0 0 0 2px ${secretFocused ? ACCENT : CHROME_DIM}`, outline: "none",
                  transition: "box-shadow .15s", marginBottom: 12,
                }}
              />
              {rotateError && (
                <div style={{ fontSize: 12, color: RED_DEEP, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={12} />{rotateError}
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <Btn variant="ghost" size="sm" onClick={() => { setRotateTarget(null); setSecretInput(""); }} style={{ flex: 1 }}>Cancel</Btn>
                <Btn variant="ink" size="sm" onClick={confirmRotate} disabled={rotating === rotateTarget.key_type} style={{ flex: 1 }}>
                  {rotating === rotateTarget.key_type ? <><Spinner size={14} />Rotating…</> : <><RefreshCw size={14} strokeWidth={2.6} />Rotate</>}
                </Btn>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── KEYS MODAL ─── */}
      <AnimatePresence>
        {keysModal && (
          <motion.div variants={modalOverlay} initial="hidden" animate="visible" exit="exit"
            onClick={() => setKeysModal(null)}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(6,14,56,.6)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <motion.div variants={modalContent} initial="hidden" animate="visible" exit="exit"
              onClick={(e: React.MouseEvent) => e.stopPropagation()} role="dialog" aria-label="Your API keys"
              style={plateStyle({ width: "100%", maxWidth: 600, maxHeight: "90vh", overflowY: "auto", padding: isMobile ? 22 : 34 })}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
                <div>
                  <h2 style={heading(isMobile ? 22 : 25)}>Your API keys</h2>
                  {keysModal.accountId && (
                    <div style={mono({ color: FAINT, marginTop: 8 })}>
                      Account: <span style={{ textTransform: "none", letterSpacing: 0, color: SLATE }}>{keysModal.accountId}</span>
                    </div>
                  )}
                </div>
                <Bead size={36} tone="chrome" onClick={() => setKeysModal(null)} ariaLabel="Close">
                  <X size={15} strokeWidth={3} />
                </Bead>
              </div>

              <div style={{ background: GOLD.soft, borderRadius: 16, boxShadow: `inset 0 0 0 1.5px ${GOLD.base}55`, padding: "14px 18px", marginBottom: 26, fontSize: 12.5, color: GOLD.deep, lineHeight: 1.7 }}>
                <strong>Copy these now.</strong> Secret keys are shown once — after this only their hashes are stored, so we can't show them again.
              </div>

              {KEY_LABELS.filter(({ key, alt }) => keysModal.keys[key] || keysModal.keys[alt]).map(({ label, key, alt }) => {
                const value = keysModal.keys[key] || keysModal.keys[alt] || "—";
                const isCopied = copied === key;
                return (
                  <div key={key} style={{ marginBottom: 16 }}>
                    <label style={mono({ color: FAINT, display: "block", marginBottom: 6 })}>{label}</label>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <div style={{
                        flex: 1, minWidth: 0, background: MIST, borderRadius: 14, padding: "11px 14px",
                        fontSize: 11.5, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        fontFamily: "'IBM Plex Mono',ui-monospace,monospace",
                      }}>
                        {value}
                      </div>
                      <Btn
                        variant="ghost" size="sm" onClick={() => copyValue(key, value)}
                        style={isCopied ? { background: SAGE_SOFT, color: SAGE_DEEP, boxShadow: `inset 0 0 0 2.5px ${SAGE}` } : undefined}
                      >
                        {isCopied ? <><Check size={13} strokeWidth={3} /> Copied</> : "Copy"}
                      </Btn>
                    </div>
                  </div>
                );
              })}

              <Btn variant="primary" onClick={() => setKeysModal(null)} style={{ width: "100%", marginTop: 12 }}>
                I've saved my keys
              </Btn>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}